/**
 * Diagnostics for a failed app start-up.
 *
 * The loading screen runs a chain of requests and used to collapse every failure
 * into one sentence, so a report from the field carried nothing that told one
 * cause from another. These helpers turn a failure into a few plain lines the
 * user can screenshot or copy into a report.
 *
 * Nothing here may ever include a credential: the lines end up in screenshots and
 * emails.
 */

const MAX_ERROR_LENGTH = 300;

/** A failure in one named stage of the start-up chain. */
export class StartupStepError extends Error {
  readonly step: string;
  readonly position: number;
  readonly total: number;
  readonly cause: unknown;
  /** Set when the failure has a specific, user-actionable headline. */
  readonly headline?: string;

  constructor(args: {
    step: string;
    position: number;
    total: number;
    cause: unknown;
    headline?: string;
  }) {
    super(describeError(args.cause));
    this.name = 'StartupStepError';
    this.step = args.step;
    this.position = args.position;
    this.total = args.total;
    this.cause = args.cause;
    this.headline = args.headline;
  }
}

/**
 * Best-effort text for anything that can be thrown at us. Thunks reject with a
 * real Error, `.unwrap()` rejects with a serialized `{ message }` object, and a
 * few preconditions reject with a bare string.
 */
export function describeError(error: unknown): string {
  let text = 'Unknown error';
  if (typeof error === 'string' && error) {
    text = error;
  } else if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message: unknown }).message;
    if (message) text = String(message);
  }
  // Tokens travel in query strings in this API. An error body should never echo
  // one back, but these lines get shared, so scrub regardless.
  text = text.replace(/(access_token|id_token)=[^&\s"']+/gi, '$1=[redacted]');
  return text.length > MAX_ERROR_LENGTH ? `${text.slice(0, MAX_ERROR_LENGTH)}…` : text;
}

export type StartupContext = {
  accountId?: number | string | null;
  /** The county group the requests were scoped to. */
  activeGroup?: number | string | null;
  /** The user's own Palmetto group, which differs from activeGroup for state users. */
  userGroup?: number | string | null;
  dataGatheringIncident?: number | string | null;
  /** The app's own connectivity flag. */
  appOnline?: boolean;
  /** Whether the session holds a Cognito ID token. Presence only -- never the token. */
  idTokenPresent?: boolean;
  /** Session expiry in epoch SECONDS (Cognito `exp`), or null when unknown. */
  sessionExpiresAt?: number | null;
};

/** "in 5h 02m" / "EXPIRED 2h 10m ago". The damage assessment API's authorizer
 *  rejects an expired or absent ID token with a bare 401, so this one line
 *  separates "session problem" from "everything else". */
function describeExpiry(expiresAt: number | null | undefined): string {
  if (!expiresAt) return 'expiry unknown';
  const deltaSeconds = expiresAt - Math.floor(Date.now() / 1000);
  const minutes = Math.floor(Math.abs(deltaSeconds) / 60);
  const span = `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}m`;
  const when = new Date(expiresAt * 1000).toISOString();
  return deltaSeconds >= 0 ? `expires ${when} (in ${span})` : `EXPIRED ${span} ago (${when})`;
}

const orNone = (value: unknown) =>
  value === null || value === undefined || value === '' ? 'none' : String(value);

/** One short line per fact, in the order someone triaging a report wants them. */
export function buildStartupDiagnostics(error: unknown, context: StartupContext): string[] {
  const lines: string[] = [];
  if (error instanceof StartupStepError) {
    lines.push(`Step: ${error.position} of ${error.total} (${error.step})`);
  }
  lines.push(`Error: ${describeError(error)}`);
  lines.push(
    `Account: ${orNone(context.accountId)} | County group: ${orNone(context.activeGroup)}` +
      ` | User group: ${orNone(context.userGroup)}`
  );
  lines.push(`Data gathering incident: ${orNone(context.dataGatheringIncident)}`);
  lines.push(
    `Sign-in: ID token ${context.idTokenPresent ? 'present' : 'MISSING'}, ` +
      describeExpiry(context.sessionExpiresAt)
  );
  lines.push(
    `Online: app=${context.appOnline ? 'yes' : 'no'}, browser=${navigator.onLine ? 'yes' : 'no'}`
  );
  lines.push(`Time: ${new Date().toISOString()}`);
  // replaced at build time by @rollup/plugin-replace (see vite.config.ts)
  lines.push(`Build: ${'__DATE__'}`);
  return lines;
}
